import React, { useMemo, useRef } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDisplayDate, getLocalISODate } from "../utils";
import { DesktopIcon, MonitorIcon } from "../../admin/components/Shared";

const LaptopIcon = ({ className = "w-3.5 h-3.5 mr-2" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="18" height="12" x="3" y="4" rx="2" ry="2" />
    <line x1="2" x2="22" y1="20" y2="20" />
  </svg>
);

const CalendarIcon = () => (
  <svg
    className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 mb-0.5 sm:mb-1 lg:mb-2"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
    <line x1="16" x2="16" y1="2" y2="6" />
    <line x1="8" x2="8" y1="2" y2="6" />
    <line x1="3" x2="21" y1="10" y2="10" />
  </svg>
);

const UserBookingHeader = ({
  selectedDate,
  setSelectedDate,
  requirement,
  setRequirement,
  userSeatCapability = "laptop",
  isLaptopFull = false,
}) => {
  const dateInputRef = useRef(null);

  const minDate = useMemo(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const maxDate = useMemo(() => {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 30);
    const yyyy = futureDate.getFullYear();
    const mm = String(futureDate.getMonth() + 1).padStart(2, "0");
    const dd = String(futureDate.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const dates = useMemo(() => {
    const today = new Date();
    const list = [];
    let i = 0;
    let added = 0;
    while (added < 7) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const day = d.getDay();
      if (day !== 0 && day !== 6) {
        const iso = getLocalISODate(d);
        const dayName = d
          .toLocaleDateString("en-US", { weekday: "short" })
          .toUpperCase();
        const dayNum = d.getDate();
        list.push({ iso, dayName, dayNum, isToday: i === 0 });
        added++;
      }
      i++;
    }
    return list;
  }, []);

  const isCustomSelected =
    selectedDate !== null && !dates.some((d) => d.iso === selectedDate);

  const handleCustomClick = () => {
    if (dateInputRef.current) {
      if (typeof dateInputRef.current.showPicker === "function") {
        dateInputRef.current.showPicker();
      } else {
        dateInputRef.current.click();
      }
    }
  };

  return (
    <div className="mb-10 rounded-[24px] bg-black p-6 text-white shadow-2xl relative overflow-hidden border border-slate-900">
      {/* Dynamic Dot Pattern with Linear Gradient Fade */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.22]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.4) 1px, transparent 0px)`,
          backgroundSize: "22px 22px",
          maskImage: "linear-gradient(to right, transparent 20%, black 70%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 20%, black 70%)",
        }}
      />

      <div className="relative space-y-4">
        {/* Row 1: Title and Hardware Tabs */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-xl font-black tracking-tight bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
              Reserve Your Space
            </h1>
            <p className="mt-1 text-[12px] font-light text-slate-500 tracking-wide">
              Secure a workspace that matches your exact requirements for the
              day.
            </p>
          </div>

          <div className="flex flex-col w-full md:w-auto">
            <div
              className="
      grid grid-cols-3 gap-2
      sm:flex sm:gap-0
      p-2
      rounded-xl
      bg-[#1e1e1e]/60
      border border-white/10
      backdrop-blur-md
    "
            >
              {[
                { id: "laptop", label: "Laptop", Icon: LaptopIcon },
                { id: "monitor", label: "Monitor", Icon: MonitorIcon },
                { id: "desktop", label: "Desktop", Icon: DesktopIcon },
              ].map(({ id, label, Icon }) => {
                let isDisabled = false;

                if (userSeatCapability === "laptop") {
                  if (isLaptopFull) {
                    if (id === "laptop") isDisabled = true;
                  } else {
                    if (id !== "laptop") isDisabled = true;
                  }
                } else {
                  if (userSeatCapability === "monitor" && id !== "monitor") {
                    isDisabled = true;
                  }

                  if (userSeatCapability === "desktop" && id !== "desktop") {
                    isDisabled = true;
                  }
                }

                return (
                  <button
                    key={id}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => setRequirement(id)}
                    className={`
            flex items-center justify-center
            w-full
            px-2 py-2
            sm:px-5 sm:py-2
            text-[10px] sm:text-[11px]
            font-black uppercase tracking-wider
            rounded-lg
            transition-all

            ${isDisabled ? "opacity-30 cursor-not-allowed text-slate-500" : ""}

            ${
              !isDisabled && requirement === id
                ? "bg-white text-black shadow-xl"
                : !isDisabled
                  ? "text-slate-400 hover:text-white"
                  : ""
            }
          `}
                  >
                    <Icon className="mr-2 h-3.5 w-3.5" />
                    <span className="hidden xs:inline">{label}</span>
                    <span className="xs:hidden">
                      {label === "Laptop"
                        ? "Laptop"
                        : label === "Monitor"
                          ? "Monitor"
                          : "Desktop"}
                    </span>
                  </button>
                );
              })}
            </div>

            {userSeatCapability === "laptop" && isLaptopFull && (
              <p className="mt-2 text-center text-xs font-semibold text-amber-400 px-2">
                Laptop seats are full. Try booking a Desktop or Monitor instead.
              </p>
            )}
          </div>
        </div>

        <div className="h-px w-full bg-white/10" />

        {/* Row 2: Date Selection */}
        <div className="space-y-3">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-500">
            SELECT BOOKING DATE
          </p>

          <div className="flex overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 gap-2 sm:gap-3 thin-horizontal-scroll scrollbar-thin scrollbar-thumb-white/10">
            {dates.map((date) => (
              <button
                key={date.iso}
                type="button"
                onClick={() => setSelectedDate(date.iso)}
                className={`flex flex-col items-center justify-center min-w-[56px] sm:min-w-[64px] lg:min-w-[80px] h-[56px] sm:h-[64px] lg:h-[80px] rounded-xl border transition-all duration-300 shrink-0 ${
                  selectedDate === date.iso
                    ? "bg-white border-white shadow-[0_20px_40px_-10px_rgba(255,255,255,0.2)] scale-105"
                    : "bg-[#1e1e1e]/60 border-white/5 hover:bg-[#262626]/80"
                }`}
              >
                <span
                  className={`text-[7px] sm:text-[8px] lg:text-[10px] font-black uppercase tracking-widest mb-0.5 lg:mb-1 ${
                    selectedDate === date.iso
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  {date.isToday ? "TODAY" : date.dayName}
                </span>
                <span
                  className={`text-md sm:text-lg lg:text-2xl font-black ${
                    selectedDate === date.iso ? "text-black" : "text-white"
                  }`}
                >
                  {date.dayNum}
                </span>
              </button>
            ))}

            {/* Custom Date Picker Trigger */}
            <div className="relative shrink-0 user-custom-date-picker">
              <DatePicker
                selected={isCustomSelected ? new Date(selectedDate) : null}
                onChange={(date) => {
                  if (date) setSelectedDate(getLocalISODate(date));
                }}
                minDate={new Date(minDate)}
                maxDate={new Date(maxDate)}
                filterDate={(date) =>
                  date.getDay() !== 0 && date.getDay() !== 6
                }
                portalId="root"
                customInput={
                  <button
                    type="button"
                    className={`flex flex-col items-center justify-center min-w-[56px] sm:min-w-[64px] lg:min-w-[80px] h-[56px] sm:h-[64px] lg:h-[80px] rounded-xl border transition-all duration-300 shrink-0 ${
                      isCustomSelected
                        ? "bg-white border-white shadow-[0_20px_40px_-10px_rgba(255,255,255,0.2)] scale-105 text-black"
                        : "bg-[#1e1e1e]/60 border-white/5 hover:bg-[#262626]/80 text-slate-400"
                    }`}
                  >
                    <CalendarIcon />
                    <span className="text-[7px] sm:text-[8px] lg:text-[10px] font-black uppercase tracking-widest mt-1">
                      {isCustomSelected
                        ? formatDisplayDate(selectedDate)
                        : "CUSTOM"}
                    </span>
                  </button>
                }
              />
            </div>
          </div>
          {!selectedDate && (
            <p className="mt-2 text-xs font-semibold text-amber-400">
              Please select a date to view available seats and continue booking.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserBookingHeader;
